import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium font-sans transition-all duration-200 ease-in-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
  {
    variants: {
      variant: {
        default: "bg-brand-primary text-white hover:bg-brand-primary/90 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-md focus-visible:ring-4 focus-visible:ring-brand-primary/20 dark:bg-brand-primary dark:hover:bg-brand-primary/80",
        destructive:
          "bg-error text-white hover:bg-error/90 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-md focus-visible:ring-4 focus-visible:ring-error/20",
        outline:
          "border border-border bg-background shadow-sm hover:bg-muted hover:text-foreground hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-4 focus-visible:ring-brand-primary/20",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-4 focus-visible:ring-secondary/20",
        ghost:
          "hover:bg-muted hover:text-foreground hover:shadow-sm focus-visible:ring-4 focus-visible:ring-brand-primary/20",
        link: "text-brand-primary underline-offset-4 hover:underline focus-visible:ring-4 focus-visible:ring-brand-primary/20",
        accent: "bg-brand-accent text-slate-900 hover:bg-brand-accent/90 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-md focus-visible:ring-4 focus-visible:ring-brand-accent/20 font-semibold",
        jema: "bg-gradient-to-r from-[#166B93] to-[#166B93]/90 text-white hover:from-[#166B93]/90 hover:to-[#166B93]/80 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-lg focus-visible:ring-4 focus-visible:ring-[#166B93]/20 font-semibold transition-all duration-200",
      },
      size: {
        default: "h-9 px-4 py-2 rounded-xl has-[>svg]:px-3",
        sm: "h-8 rounded-xl gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-xl px-6 has-[>svg]:px-4",
        xl: "h-12 rounded-xl px-8 has-[>svg]:px-6 text-base",
        icon: "size-9 rounded-xl",
        "icon-sm": "size-8 rounded-xl",
        "icon-lg": "size-10 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})

Button.displayName = "Button"

export { Button, buttonVariants }
